import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "motion/react";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import { Renderer } from "@json-render/react";

import { useAuth } from "../contexts/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import useResultsHistory from "../hooks/useResultsHistory";
import Message from "../components/ui-new/Message";
import MessageInput from "../components/ui-new/MessageInput";
import ScrollIndicator from "../components/ui-new/ScrollIndicator";
// ResultGrid removed - products now render inline via json-render
import ResultGridInline from "../components/ui-new/ResultGridInline";
import { useModalContext } from "../components/contexts/ModalContext";
import { useUserSessions } from "../api/useUserSessions";
import { useAnonymousSession } from "../api/useAnonymousSession";
import { useJsonRenderStream } from "../hooks/useJsonRenderStream";
import { useWallet } from "../contexts/WalletContext";
import { useBrand } from "../contexts/BrandContext";
import { componentRegistry } from "../components/json-render/registry";
import { SimpleCommerceProvider } from "../components/json-render/Providers";
import { PreviewPanel } from "../components/PreviewPanel";

const STARTING_POLLING_INTEVERAL = 1000;

const fetchInferPreferences = async ({ userId, messages }) => {
  const apiBaseUrl = `https://agentic-commerce-digital-twin.philip-134.workers.dev`;

  const response = await fetch(`${apiBaseUrl}/infer`, {
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify({ userId, messages }),
  });

  const data = await response.json();

  return data;
};

const fetchUpdateTwin = async ({ userId, preferences }) => {
  try {
    const apiBaseUrl = `https://agentic-commerce-digital-twin.philip-134.workers.dev`;
    const response = await fetch(`${apiBaseUrl}/twin`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ userId, preferences }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update twin: ${response.status}`);
    }

    // Just return nothing on success
    return;
  } catch (error) {
    console.error("Error updating twin:", error);
  }
};

// Feature flag for progressive streaming
const USE_PROGRESSIVE_STREAMING = true;

export default function ResultsPage({ setSelectedProduct }) {
  const {
    messages,
    message,
    updateMessage,
    sendMessage,
    setMessages,
    scrollContainerRef,
    inferredPreferences,
    setInferredPreferences,
  } = useOutletContext();

  // Define all hooks before conditional logic
  const isMobile = useIsMobile();
  const { isLoading } = useAuth();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { setIsModalOpen } = useModalContext();
  const isFirstLoad = useRef(true);
  const { user } = useAuth();
  const { walletToken } = useWallet();
  const { brand } = useBrand();

  // polling and results (legacy)
  const [polling, setPolling] = useState(false);
  const pollingInterval = useRef(STARTING_POLLING_INTEVERAL);
  const [results, setResults] = useState(null);
  const activePoll = useRef(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  // T030: Exploration mode state
  const [explorationActive, setExplorationActive] = useState(false);

  // Progressive streaming state
  const lastStreamedQuery = useRef(null);
  const currentStreamingQuery = useRef(null); // Track which query is currently streaming
  const messageTreesRef = useRef({}); // Map of query content -> completed tree
  const [completedTrees, setCompletedTrees] = useState({}); // Trigger re-render when trees complete

  const {
    tree,
    isStreaming,
    error: streamError,
    progress,
    send: sendStream,
    clear: clearStream
  } = useJsonRenderStream({
    sessionId,
    baseUrl: '',
    authToken: walletToken, // Pass wallet token for personalization
    usePublicEndpoint: !walletToken, // Use secure endpoint when authenticated
    onError: (err) => console.error('[Stream] Error:', err),
    onComplete: () => console.log('[Stream] Complete'),
  });

  // Track the previous streaming state to detect when streaming finishes
  const wasStreamingRef = useRef(false);

  // Save completed tree when streaming finishes
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && tree && currentStreamingQuery.current) {
      // Streaming just finished - save the tree
      console.log('[Stream] Saving completed tree for query:', currentStreamingQuery.current);
      messageTreesRef.current[currentStreamingQuery.current] = tree;
      setCompletedTrees(prev => ({
        ...prev,
        [currentStreamingQuery.current]: true
      }));
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, tree]);

  // T030: Detect ExplorationGrid in tree and activate exploration mode
  useEffect(() => {
    if (tree && tree.elements) {
      const hasExplorationGrid = Object.values(tree.elements).some(el => el.type === 'ExplorationGrid');
      if (hasExplorationGrid && !explorationActive) {
        setExplorationActive(true);
      }
    }
  }, [tree, explorationActive]);

  const { isSignedIn, getUserSessionChat, queryVectorStore } =
    useUserSessions();
  const { getAnonymousChat, queryVectorStore: anonymousQueryVectorStore } =
    useAnonymousSession();

  const updateInferredPreferences = useCallback(
    async (currentMessages) => {
      try {
        const preferences = await fetchInferPreferences({
          userId: user?.id || "anonymous",
          messages: currentMessages,
        });

        setInferredPreferences(preferences);
      } catch (error) {
        console.error("Error fetching inferred preferences:", error);
      }
    },
    [user?.id, setInferredPreferences]
  );

  const { getIndex } = useResultsHistory(sessionId, messages, results);

  const [inputHeight, setInputHeight] = useState(48); // Default height
  const [expandedMessageIndex, setExpandedMessageIndex] = useState(null); // Track which message's grid is expanded

  useEffect(() => {
    isFirstLoad.current = true;
    setPolling(false); // Don't start polling yet, wait for inferredPreferences
    setResults(null);
    setInferredPreferences(null);
    setLastMessageCount(0);
    lastStreamedQuery.current = null;
    currentStreamingQuery.current = null;
    messageTreesRef.current = {}; // Reset completed trees for new session
    setCompletedTrees({});
    setExplorationActive(false); // T044: Reset exploration mode on new conversation
    clearStream();
  }, [sessionId, setInferredPreferences, clearStream]);

  // Progressive streaming: trigger when new user message is added
  useEffect(() => {
    if (!USE_PROGRESSIVE_STREAMING) return;
    if (!messages || messages.length === 0) return;

    // Find the last user message
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUserMessage) return;

    const query = lastUserMessage.content;

    // Only stream if this is a new query we haven't streamed yet
    if (query && query !== lastStreamedQuery.current && !isStreaming) {
      console.log('[Stream] New user message detected, starting stream:', query);
      lastStreamedQuery.current = query;
      currentStreamingQuery.current = query; // Track which query is streaming
      sendStream(query, { cartItemCount: 0, preferences: inferredPreferences || {}, tenantId: brand?.tenantId });
    }
  }, [messages, sendStream, isStreaming, inferredPreferences, brand?.tenantId]);

  // Update inferred preferences when message count changes
  useEffect(() => {
    const currentMessageCount = messages?.length;

    // Only update if the message count has changed and we have messages
    if (currentMessageCount > 0 && currentMessageCount !== lastMessageCount) {
      updateInferredPreferences(messages);
      setLastMessageCount(currentMessageCount);
    }
  }, [messages, lastMessageCount, updateInferredPreferences]);

  // Start polling only when inferredPreferences exist (legacy - disabled when streaming)
  useEffect(() => {
    if (USE_PROGRESSIVE_STREAMING) return; // Skip polling when streaming is enabled

    if (sessionId && inferredPreferences) {
      console.log(
        "Starting polling with inferredPreferences:",
        inferredPreferences
      );
      setPolling(true);
      pollingInterval.current = STARTING_POLLING_INTEVERAL; // Reset interval
    } else {
      setPolling(false);
    }
  }, [sessionId, inferredPreferences]);

  // NEW - to load the chat if on id page
  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!sessionId) navigate("/");

    const loadExistingChat = async () => {
      try {
        const chatData = isSignedIn
          ? await getUserSessionChat(sessionId)
          : await getAnonymousChat(sessionId).catch(() => {
              setIsModalOpen(true);
            });

        if (chatData && chatData.messages.length > 0) {
          setMessages(chatData.messages);

          // If chat has requirements, use them to set inferred preferences
          if (chatData.requirements) {
            console.log(
              "Found requirements in chat data:",
              chatData.requirements
            );

            // Ensure requirements object has necessary structure and fields
            const formattedRequirements =
              typeof chatData.requirements === "string"
                ? { requirementsSummary: chatData.requirements }
                : chatData.requirements;

            // Add description field if missing but requirementsSummary exists
            if (
              formattedRequirements.requirementsSummary &&
              !formattedRequirements.description
            ) {
              formattedRequirements.description =
                formattedRequirements.requirementsSummary;
            }

            setInferredPreferences(formattedRequirements);
          } else {
            console.log("No requirements found in chat data");
          }

          // Start polling when a new chat is loaded (legacy)
          if (!USE_PROGRESSIVE_STREAMING) {
            setPolling(true);
            pollingInterval.current = STARTING_POLLING_INTEVERAL;
          }
        }
      } catch (error) {
        console.error("Error loading chat:", error);
        // Session doesn't exist - redirect to create a new one
        navigate("/");
      }
    };

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      loadExistingChat();
    }
  }, [
    getAnonymousChat,
    getUserSessionChat,
    isSignedIn,
    navigate,
    sessionId,
    setInferredPreferences,
    setIsModalOpen,
    setMessages,
    isLoading,
  ]);

  // Polling mechanism (legacy - disabled when streaming)
  useEffect(() => {
    if (USE_PROGRESSIVE_STREAMING) return; // Skip polling when streaming is enabled

    if (isLoading || !inferredPreferences) {
      return;
    }

    let timerId = null;

    const pollResults = async () => {
      if (!polling) {
        activePoll.current = false;
        return;
      }

      // Log polling attempt for debugging
      console.log(
        "Polling attempt with inferredPreferences:",
        inferredPreferences
      );

      activePoll.current = true;

      try {
        const query = inferredPreferences.description;

        console.log("Executing query with:", query);

        const results = isSignedIn
          ? await queryVectorStore(query, inferredPreferences)
          : await anonymousQueryVectorStore(query, inferredPreferences).catch(
              () => {
                setIsModalOpen(true);
              }
            );

        // Transform the matches array to only contain metadata
        const allResults = results.data.matches.map((match) => ({
          ...match.metadata,
          aboveThreshold: match.score >= 0.7,
        }));

        setResults(allResults);

        // Stop polling after getting results - no need to keep fetching
        if (allResults.length > 0) {
          console.log("Polling stopped - results received:", allResults.length);
          setPolling(false);
          activePoll.current = false;
          return;
        }

        // If no results, increase interval and retry
        const newInterval = pollingInterval.current * 1.5;

        // If interval exceeds 60s, stop polling
        if (newInterval > 60000) {
          console.log("Polling stopped - max interval reached");
          setPolling(false);
          activePoll.current = false;
          return;
        }

        pollingInterval.current = newInterval;
        activePoll.current = false;
      } catch (error) {
        console.error("Error while polling for results:", error);
      }
    };

    if (polling && !activePoll.current) {
      // Run immediately when polling is set to true
      pollResults();

      // Then schedule the next poll with the current interval
      clearTimeout(timerId);
      timerId = setTimeout(pollResults, pollingInterval.current);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [
    anonymousQueryVectorStore,
    inferredPreferences,
    isSignedIn,
    polling,
    queryVectorStore,
    setIsModalOpen,
    isLoading,
  ]);

  //update the twin with clicked preference
  const updateTwin = async (preference) => {
    console.log("Updating twin with preference:", {
      userId: user?.id,
      preferences: preference,
    });

    fetchUpdateTwin({
      userId: user?.id,
      preferences: preference,
    });

    // Remove the clicked preference from inferredPreferences
    const [category, [value]] = Object.entries(preference)[0];

    setInferredPreferences((prev) => {
      if (!prev || !prev[category]) return prev;

      const updatedPreferences = { ...prev };
      const updatedCategoryPreferences = updatedPreferences[category].filter(
        (pref) => pref !== value
      );

      if (updatedCategoryPreferences.length === 0) {
        // If no preferences left in category, remove the category
        delete updatedPreferences[category];
      } else {
        // Otherwise update the category with remaining preferences
        updatedPreferences[category] = updatedCategoryPreferences;
      }

      return Object.keys(updatedPreferences).length === 0
        ? null
        : updatedPreferences;
    });
  };

  // Handle actions from json-render components
  const handleAction = useCallback((action) => {
    console.log('[Stream] Action:', action);
    if (action.name === 'view_details' && action.params?.productId) {
      // Find product in tree and set as selected
      const elements = tree?.elements || {};
      for (const el of Object.values(elements)) {
        if (el.props?.productId === action.params.productId) {
          setSelectedProduct({
            id: el.props.productId,
            title: el.props.title,
            brand: el.props.brand,
            price: el.props.price,
            image: el.props.image,
            ...el.props,
          });
          break;
        }
      }
    } else if (action.name === 'exit_exploration') {
      // T030: Exit exploration mode via "Back to chat" button
      setExplorationActive(false);
    } else if (action.name === 'refine_search' && action.params?.query) {
      // Trigger a new search with the suggestion
      const query = action.params.query;
      console.log('[Stream] Refine search triggered:', query);

      // Add user message to chat history (without calling chat API)
      // We manually add to messages instead of calling sendMessage to avoid triggering the chat API
      const newUserMessage = { role: 'user', content: query };
      setMessages(prev => [...prev, newUserMessage]);

      // Clear previous stream and trigger new product search
      clearStream();
      lastStreamedQuery.current = query;
      currentStreamingQuery.current = query;
      sendStream(query, { cartItemCount: 0, preferences: inferredPreferences || {}, tenantId: brand?.tenantId });
    }
  }, [tree, setSelectedProduct, setMessages, clearStream, sendStream, inferredPreferences, brand?.tenantId]);

  // Custom submit handler for streaming mode - bypasses chat API
  const handleStreamingSubmit = useCallback((event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    const messageToSend = message.trim();
    if (!messageToSend) return;

    console.log('[Stream] Submitting message (streaming mode):', messageToSend);

    // T030: Exit exploration mode when user sends a message
    if (explorationActive) {
      // T033: Emit exploration.exited TES event for message-based exit
      console.log('[TES Event] exploration.exited', {
        exit_method: 'new_message',
        products_viewed: 0,
        duration_ms: 0,
      });
    }
    setExplorationActive(false);

    // Add user message to state - effect will detect and trigger sendStream
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);

    // Clear the input
    updateMessage('');
  }, [message, setMessages, updateMessage]);

  // Custom style for WebKit browsers (Chrome, Safari)
  const webkitScrollbarStyles = `
    .hide-scrollbar::-webkit-scrollbar {
      display: none;
    }
  `;

  const hideScrollbarStyles = {
    msOverflowStyle: "none", // IE and Edge
    scrollbarWidth: "none", // Firefox
    overflow: "auto", // Basic styling
  };

  return (
    <div
      className={`relative min-h-[100dvh] w-full flex font-outfit font-light${expandedMessageIndex !== null ? " z-50 bg-white sm:bg-transparent" : " z-30"}`}
    >
      <style>{webkitScrollbarStyles}</style>
      <motion.div
        className="w-full h-[100dvh] relative"
        initial={
          window.innerWidth >= 768
            ? { opacity: 0, x: 50 }
            : { opacity: 1, x: 0 }
        }
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Scrollable message container with hidden scrollbar */}
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto pb-32 hide-scrollbar relative"
          style={{
            ...hideScrollbarStyles,
            paddingBottom: `${inputHeight + 8}px`,
          }}
        >
          {/* Message flow container - centered for conversational UI, full-width in exploration mode */}
          <div className={`message-flow flex flex-col space-y-4 items-start relative px-4 py-6 pt-20 w-full transition-all duration-300 ${
            explorationActive ? '' : 'max-w-2xl mx-auto'
          }`}>
            {/* All messages in chronological order */}
            {(messages || []).map((msg, index) => (
              <React.Fragment key={index}>
                {/* T031: Hide message bubbles when exploration mode is active */}
                <div className={`w-full transition-all duration-300 ${
                  explorationActive ? 'max-h-0 opacity-0 overflow-hidden' : ''
                }`}>
                  <Message
                    sender={msg.role === "user" ? "user" : "bot"}
                  >
                    {msg.content}
                  </Message>
                </div>
                {/* Inline products - render after each user message with its associated tree */}
                {msg.role === "user" && USE_PROGRESSIVE_STREAMING && (() => {
                  const query = msg.content;
                  const isCurrentlyStreaming = currentStreamingQuery.current === query && isStreaming;
                  // Use completedTrees state to ensure re-render when tree finishes
                  const hasCompletedTree = completedTrees[query];
                  const completedTree = hasCompletedTree ? messageTreesRef.current[query] : null;
                  const treeToRender = isCurrentlyStreaming ? tree : completedTree;

                  // Show tree if we have one (either streaming or completed)
                  if (!treeToRender && !isCurrentlyStreaming) return null;

                  return (
                    <div className="w-full mt-2">
                      {/* Progress indicator - only for currently streaming message */}
                      {isCurrentlyStreaming && progress && (
                        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gray-50 border border-gray-100 mb-3">
                          <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm text-gray-600">{progress}</span>
                        </div>
                      )}
                      {/* Streaming error - only for currently streaming message */}
                      {isCurrentlyStreaming && streamError && (
                        <div className="text-sm text-red-500 mb-3">
                          Error: {streamError.message}
                        </div>
                      )}
                      {/* Inline rendered products */}
                      {treeToRender && (
                        <SimpleCommerceProvider sessionId={sessionId} onAction={handleAction} inline>
                          <Renderer
                            tree={treeToRender}
                            registry={componentRegistry}
                            loading={isCurrentlyStreaming}
                          />
                        </SimpleCommerceProvider>
                      )}
                    </div>
                  );
                })()}
                {/* Legacy mobile results - for non-streaming mode */}
                {isMobile && msg.role === "user" && !USE_PROGRESSIVE_STREAMING && (
                  <ResultGridInline
                    setSelectedProduct={setSelectedProduct}
                    results={getIndex(index)}
                    isPolling={
                      polling &&
                      index === messages.findLastIndex((m) => m.role === "user")
                    }
                    isExpanded={expandedMessageIndex === index}
                    setIsExpanded={(value) =>
                      setExpandedMessageIndex(value ? index : null)
                    }
                    streamingTree={null}
                    isStreaming={false}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Note: Per-message progress is shown inside each user message block above */}
          </div>
        </div>

        {/* Custom Scroll Indicator */}
        <ScrollIndicator scrollContainerRef={scrollContainerRef} />

        <div
          className={`p-0 md:p-4 absolute bottom-0 left-0 right-0 mx-auto max-w-2xl z-30 bg-gradient-to-t from-white to-transparent from-10% to-30% pb-[env(safe-area-inset-bottom)]`}
        >
          <MessageInput
            value={message}
            onChange={updateMessage}
            onSubmit={USE_PROGRESSIVE_STREAMING ? handleStreamingSubmit : sendMessage}
            placeholder={<div>Tell me more...</div>}
            buttonText="Send"
            disabled={!message.trim()}
            inferredPreferences={inferredPreferences}
            onPreferenceClick={updateTwin}
            onHeightChange={setInputHeight}
          />
        </div>
      </motion.div>
      {/* Preview panel for product details and grid views */}
      <PreviewPanel />
    </div>
  );
}
